/**
 * StudentsTab – every student at the school and the classes they are in, by
 * approval status. Pending students are reviewed here when no class teacher can
 * (or the school steps in); approved ones in no class are placed in bulk.
 */

import { useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { DataTable, type Column } from '../analytics/DataTable';
import { AnalyticsEmptyState, AnalyticsPanel, TableSkeleton } from '../analytics/AnalyticsPanel';
import type { RosterPerson, SchoolRoster } from '../../hooks/useSchoolRoster';
import { changeClassStudents, describeStudentChange } from '../../services/schoolRosterService';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { StatusBadge } from './TeacherDrawer';
import { classLabel } from './ClassDrawer';
import { StudentDrawer } from './StudentDrawer';

const SELECT = 'h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground';

export type StudentView = 'all' | 'unplaced' | 'pending' | 'approved' | 'rejected';
const VIEWS: Array<{ key: StudentView; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'unplaced', label: 'No class' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

export function StudentsTab({
  roster,
  schoolId,
  view,
  onView,
  openId,
  onOpen,
}: {
  roster: SchoolRoster;
  schoolId: string;
  /** From the URL, so the overview can link straight to a list. */
  view: string | null;
  onView: (view: StudentView) => void;
  /** The student whose decision drawer is open, from the URL. */
  openId: string | null;
  onOpen: (id: string | null) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  /** Class ids that still exist; a student can point at an archived or deleted class. */
  const liveClasses = (s: RosterPerson) => s.classIds.filter((id) => roster.classById.has(id));
  const statusOf = (s: RosterPerson) => s.approvalStatus ?? 'pending';
  // "No class" means approved and unplaced; a pending student is placed by approving them.
  const byView: Record<StudentView, RosterPerson[]> = {
    all: roster.students,
    unplaced: roster.students.filter((s) => statusOf(s) === 'approved' && liveClasses(s).length === 0),
    pending: roster.students.filter((s) => statusOf(s) === 'pending'),
    approved: roster.students.filter((s) => statusOf(s) === 'approved'),
    rejected: roster.students.filter((s) => statusOf(s) === 'rejected'),
  };
  const active: StudentView = VIEWS.find((v) => v.key === view)?.key ?? 'all';
  const rows = byView[active];
  const unplaced = byView.unplaced.length;
  const open = openId ? roster.students.find((s) => s.id === openId) ?? null : null;
  const selectable = (s: RosterPerson) => statusOf(s) === 'approved';

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectableRows = rows.filter(selectable);
  const allShown = selectableRows.length > 0 && selectableRows.every((s) => selected.has(s.id));

  const addToClass = async () => {
    if (selected.size > 200) {
      toast.error('Add at most 200 students at a time.');
      return;
    }
    setBusy(true);
    try {
      const r = await changeClassStudents(target, { schoolId, add: [...selected] });
      if (!r.ok) {
        toast.error(r.message || 'The students could not be added.');
        return;
      }
      const refused = r.data!.report.some((x) => x.outcome !== 'added');
      (refused ? toast.warn : toast.success)(describeStudentChange(r.data!.report));
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<RosterPerson>[] = [
    {
      // The checkbox sits with the name: the first column is each card's title on phones.
      key: 'name',
      header: 'Student',
      sortValue: (s) => s.name,
      cell: (s) => (
        <div className="flex min-w-0 items-center gap-3">
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0 accent-primary disabled:opacity-30"
            checked={selected.has(s.id)}
            disabled={!selectable(s)}
            title={selectable(s) ? undefined : 'Approve the student to place them'}
            onChange={() => toggle(s.id)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${s.name}`}
          />
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{s.name}</p>
            <p className="truncate text-xs text-muted-foreground">{s.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'classes',
      header: 'Classes',
      sortValue: (s) => liveClasses(s).length,
      cell: (s) => {
        const names = liveClasses(s).map((id) => classLabel(roster.classById.get(id)!));
        return names.length ? (
          <div className="flex flex-wrap gap-1">
            {names.map((n) => (
              <span key={n} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground">
                {n}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400">No class</span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      cell: (s) =>
        statusOf(s) === 'pending' ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(s.id);
            }}
          >
            Review
          </Button>
        ) : (
          <StatusBadge status={s.approvalStatus} />
        ),
    },
  ];

  const targetClass = target ? roster.classById.get(target) : null;
  const seats = targetClass ? MAX_CLASS_SIZE - (targetClass.student_ids ?? []).length : 0;

  return (
    <AnalyticsPanel
      title="Students"
      description={`${roster.students.length} student(s) at the school${unplaced ? `, ${unplaced} approved but in no class` : ''}.`}
      action={
        <div role="tablist" aria-label="Filter students" className="inline-flex flex-wrap rounded-lg border border-border p-0.5">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={active === v.key}
              onClick={() => {
                setSelected(new Set());
                onView(v.key);
              }}
              className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
                active === v.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {v.label} <span className="tabular-nums opacity-80">({byView[v.key].length})</span>
            </button>
          ))}
        </div>
      }
    >
      {roster.loading ? (
        <TableSkeleton />
      ) : (
        <>
          {selected.size > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-2 text-sm">
              <span className="px-1 text-foreground">{selected.size} selected</span>
              <select className={SELECT} value={target} onChange={(e) => setTarget(e.target.value)} disabled={busy} aria-label="Class to add to">
                <option value="">Add to class…</option>
                {roster.classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {classLabel(c)} ({(c.student_ids ?? []).length}/{MAX_CLASS_SIZE})
                  </option>
                ))}
              </select>
              <Button size="sm" onClick={addToClass} disabled={!target || busy}>
                {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Add
              </Button>
              {targetClass && selected.size > seats && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Only {Math.max(0, seats)} seat{seats === 1 ? '' : 's'} left
                </span>
              )}
              <button type="button" className="ml-auto px-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </div>
          )}
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(s) => s.id}
            initialSort={{ key: 'name', direction: 'asc' }}
            searchPlaceholder="Search name or email"
            searchText={(s) => `${s.name} ${s.email}`}
            onRowClick={(s) => (selectable(s) ? toggle(s.id) : onOpen(s.id))}
            toolbar={
              selectableRows.length > 0 ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setSelected(allShown ? new Set() : new Set(selectableRows.map((s) => s.id)))}
                >
                  {allShown ? 'Clear selection' : `Select all ${selectableRows.length} approved`}
                </button>
              ) : undefined
            }
            empty={
              <AnalyticsEmptyState
                compact
                title={
                  active === 'unplaced'
                    ? 'Every approved student is in a class'
                    : active === 'pending'
                      ? 'No students waiting for approval'
                      : active === 'all'
                        ? 'No students yet'
                        : `No ${active} students`
                }
                description={active === 'all' ? 'Students appear here once they join your school with its code.' : 'Nothing to do here.'}
              />
            }
          />
        </>
      )}
      {open && <StudentDrawer key={open.id} student={open} roster={roster} schoolId={schoolId} onClose={() => onOpen(null)} />}
    </AnalyticsPanel>
  );
}
